import type { JSX } from "react";
import { toEpochSeconds, toLocalDate } from "../domain/time";
import { useI18n } from "../shared/i18n/I18nContext";
import { cx } from "../shared/ui/classNames";
import { formatDate, formatTime } from "../shared/ui/format";
import styles from "../shared/ui/ui.module.css";
import { useToday } from "../shared/ui/useToday";
import { useLocalAttributions, useLocalEvents, useOffline } from "./OfflineContext";
import local from "./SyncStatusBar.module.css";
import { useSync, useSyncStatus } from "./SyncContext";
import type { SyncStatus } from "./SyncEngine";
import { useOnlineStatus } from "./useOnlineStatus";
import { useOwnerKey } from "../shared/session/tenantSelection";
import { showsSyncDetails } from "./syncDetails";
import { useNavigationRoles } from "../shared/session/useNavigationRoles";

/** Largest count the badge of the indicator shows; more entries read „99+“. */
const maxBadgeCount = 99;

/** Connectivity and outbox of the signed-in person. */
interface SyncSummary {
  /** Whether the device is online and the server answered the last attempt. */
  readonly reachable: boolean;
  /** Entries of this person and tenant not yet sent. */
  readonly pending: number;
  /** Entries the server rejected (conflicts to resolve). */
  readonly rejected: number;
  /** State of the synchronization engine. */
  readonly status: SyncStatus;
}

/**
 * Reads the connectivity and the outbox of the signed-in person and tenant.
 *
 * @returns The summary.
 */
function useSyncSummary(): SyncSummary {
  const owner = useOwnerKey();
  const online = useOnlineStatus();
  const status = useSyncStatus();
  const { events } = useLocalEvents(owner);
  const attributions = useLocalAttributions(owner);
  const pending =
    events.filter((event) => event.syncStatus === "pending").length +
    attributions.filter((attribution) => attribution.syncStatus === "pending").length;
  const rejected =
    events.filter((event) => event.syncStatus === "rejected").length +
    attributions.filter((attribution) => attribution.syncStatus === "rejected").length;
  const reachable = online && status.phase !== "offline";
  const summary: SyncSummary = { reachable, pending, rejected, status };

  return summary;
}

/** Properties of {@link CloudIcon}. */
interface CloudIconProps {
  /** Draws the cloud crossed out. */
  readonly offline: boolean;
}

/**
 * Decorative cloud of the indicator, crossed out while offline (UX-029). The state is also named
 * in text, so the icon is hidden from assistive technology.
 *
 * @param props - Component properties.
 * @returns The SVG element.
 */
function CloudIcon(props: CloudIconProps): JSX.Element {
  const cloud = "M7 19a4.5 4.5 0 0 1-.5-8.97A6 6 0 0 1 18 10.5a4.25 4.25 0 0 1-.5 8.5z";
  const paths = props.offline ? [cloud, "M3 3l18 18"] : [cloud, "M9.5 14.5l2 2l3.5-3.5"];
  const icon = (
    <svg
      className={local["icon"]}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths.map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  );

  return icon;
}

/**
 * Compact connectivity status for the sticky app bar, so it stays visible on every page and after
 * scrolling (spec 11.2, UX-102): a cloud icon, the word „Offline“ on a warning surface while there
 * is no connection (UX-029) and the number of entries not yet sent as a badge. The state word is a
 * polite live region; the count is announced by the {@link SyncStatusBar} below the app bar and is
 * therefore hidden from assistive technology here.
 *
 * @returns The indicator.
 */
export function SyncIndicator(): JSX.Element {
  const { t } = useI18n();
  const { reachable, pending } = useSyncSummary();
  const badge = pending > maxBadgeCount ? `${String(maxBadgeCount)}+` : String(pending);

  const indicator = (
    <div
      className={cx(local["indicator"], reachable ? undefined : local["offline"])}
      data-connectivity={reachable ? "online" : "offline"}
    >
      <CloudIcon offline={!reachable} />
      <span role="status" className={reachable ? "visually-hidden" : local["label"]}>
        {reachable ? t("sync.online") : t("sync.offline")}
      </span>
      {pending > 0 ? (
        <span className={local["badge"]} aria-hidden="true">
          {badge}
        </span>
      ) : null}
    </div>
  );

  return indicator;
}

/**
 * Synchronization details below the app bar (spec 8.3, 11.2): entries not yet sent, the last
 * synchronization or its failure, conflicts and sending on request. Online or offline is shown by
 * the {@link SyncIndicator} in the app bar.
 *
 * @returns The status bar.
 */
export function SyncStatusBar(): JSX.Element | null {
  const { t, languageTag } = useI18n();
  const offline = useOffline();
  const { syncNow } = useSync();
  const { reachable, pending, rejected, status } = useSyncSummary();
  const roles = useNavigationRoles();
  // In the device's time zone, like the time of the synchronization; follows midnight (F-17).
  const today = useToday(offline.nowMs, offline.deviceTimeZone);

  if (!showsSyncDetails(roles, pending, rejected)) {
    return null;
  }

  let message: string;

  if (status.phase === "syncing") {
    message = t("sync.syncing");
  } else if (pending > 0) {
    message = t("sync.pending", { count: pending });
  } else if (status.phase === "error") {
    // Entries the server accepted are not reported as failed when only its state could not be loaded (F-104).
    message = t(status.outboxDelivered ? "sync.refreshFailed" : "sync.error", {
      code: status.lastErrorCode ?? "?",
    });
  } else if (status.lastSuccessAtMs === null) {
    message = t("sync.notYet");
  } else {
    const lastSuccess = toEpochSeconds(status.lastSuccessAtMs);
    const time = formatTime(lastSuccess, languageTag, offline.deviceTimeZone);
    const date = toLocalDate(lastSuccess, offline.deviceTimeZone);
    // A synchronization of another day names its date, so "um 23:58" is not read as today (F-17).
    message =
      date === today
        ? t("sync.upToDate", { time })
        : t("sync.upToDateOn", { date: formatDate(date, languageTag), time });
  }

  const bar = (
    <div className={styles["row"]} role="status" aria-live="polite">
      <span>{message}</span>
      {rejected > 0 ? <span>{t("sync.conflicts", { count: rejected })}</span> : null}
      {pending > 0 && reachable && status.phase !== "syncing" ? (
        <button
          type="button"
          className={styles["button"]}
          onClick={() => {
            void syncNow();
          }}
        >
          {t("sync.retry")}
        </button>
      ) : null}
    </div>
  );

  return bar;
}
